import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Bell, Calendar, Clock, AlertTriangle, AlertCircle, CheckCircle,
  Phone, User, Stethoscope, Heart, Users, Activity, RefreshCw,
  ChevronRight, Settings, Volume2, Mail, MessageSquare
} from "lucide-react";

const DISCIPLINE_ICONS = {
  RN: Stethoscope,
  LPN: Stethoscope,
  SW: Users,
  Chaplain: Heart,
  MD: User,
  NP: User,
  Aide: User,
  PT: Activity,
  OT: Activity,
  ST: Activity,
  RD: User
};

const DISCIPLINE_COLORS = {
  RN: "bg-blue-100 text-blue-700 border-blue-200",
  LPN: "bg-blue-100 text-blue-700 border-blue-200",
  SW: "bg-pink-100 text-pink-700 border-pink-200",
  Chaplain: "bg-purple-100 text-purple-700 border-purple-200",
  MD: "bg-violet-100 text-violet-700 border-violet-200",
  NP: "bg-violet-100 text-violet-700 border-violet-200",
  Aide: "bg-orange-100 text-orange-700 border-orange-200",
  PT: "bg-teal-100 text-teal-700 border-teal-200",
  OT: "bg-teal-100 text-teal-700 border-teal-200",
  ST: "bg-teal-100 text-teal-700 border-teal-200",
  RD: "bg-green-100 text-green-700 border-green-200"
};

const PRIORITY_STYLES = {
  high: {
    bg: "bg-red-50 border-red-200",
    icon: AlertTriangle,
    iconColor: "text-red-500",
    badge: "bg-red-100 text-red-700"
  },
  medium: {
    bg: "bg-amber-50 border-amber-200",
    icon: AlertCircle,
    iconColor: "text-amber-500",
    badge: "bg-amber-100 text-amber-700"
  },
  low: {
    bg: "bg-green-50 border-green-200",
    icon: CheckCircle,
    iconColor: "text-green-500",
    badge: "bg-green-100 text-green-700"
  }
};

export default function VisitRemindersWidget({ className = "", compact = false }) {
  const [reminders, setReminders] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [notificationPrefs, setNotificationPrefs] = useState({
    push_enabled: true,
    sms_enabled: false,
    email_enabled: true,
    reminder_days_before: 1
  });

  const loadReminders = useCallback(async () => {
    setLoading(true);
    try {
      const [remindersRes, scheduleRes] = await Promise.all([
        api.get("/ehr/visit-frequency/reminders"),
        api.get("/ehr/visit-frequency/schedule/upcoming?days=7")
      ]);
      setReminders(remindersRes.data.reminders || []);
      setSchedule(scheduleRes.data.schedule || []);
    } catch (error) {
      console.error("Error loading reminders:", error);
      // Demo fallback
      setReminders([
        {
          id: "demo-1",
          patient_name: "Your Care",
          discipline: "RN",
          discipline_name: "Registered Nurse",
          reminder_type: "due_today",
          due_date: new Date().toISOString().split("T")[0],
          days_until_due: 0,
          staff_name: "Sarah Johnson, RN",
          staff_phone: "(555) 123-4567",
          message: "Your Registered Nurse visit is due today.",
          priority: "high"
        },
        {
          id: "demo-2",
          patient_name: "Your Care",
          discipline: "SW",
          discipline_name: "Social Worker",
          reminder_type: "due_tomorrow",
          due_date: new Date(Date.now() + 86400000).toISOString().split("T")[0],
          days_until_due: 1,
          staff_name: "Michael Chen, LCSW",
          staff_phone: "(555) 234-5678",
          message: "Your Social Worker visit is due tomorrow.",
          priority: "medium"
        }
      ]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReminders();
  }, [loadReminders]);

  const getDisciplineIcon = (discipline) => {
    const Icon = DISCIPLINE_ICONS[discipline] || User;
    return Icon;
  };

  const getPriorityStyle = (priority) => {
    return PRIORITY_STYLES[priority] || PRIORITY_STYLES.low;
  };

  const formatDueDate = (dueDate, daysUntil) => {
    if (daysUntil < 0) return `${Math.abs(daysUntil)} day(s) overdue`;
    if (daysUntil === 0) return "Today";
    if (daysUntil === 1) return "Tomorrow";
    return `In ${daysUntil} days`;
  };

  const highPriorityCount = reminders.filter(r => r.priority === "high").length;

  if (compact) {
    // Compact widget for dashboards
    return (
      <Card className={className} data-testid="visit-reminders-widget-compact">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Bell className="w-4 h-4" />
              Visit Reminders
            </CardTitle>
            {highPriorityCount > 0 && (
              <Badge className="bg-red-100 text-red-700">
                {highPriorityCount} urgent
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : reminders.length === 0 ? (
            <div className="text-center py-4 text-sm text-muted-foreground">
              <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
              All visits on track!
            </div>
          ) : (
            <div className="space-y-2">
              {reminders.slice(0, 3).map((reminder) => {
                const DisciplineIcon = getDisciplineIcon(reminder.discipline);
                const priorityStyle = getPriorityStyle(reminder.priority);
                
                return (
                  <div
                    key={reminder.id}
                    className={`p-2 rounded-lg border ${priorityStyle.bg}`}
                    data-testid={`reminder-compact-${reminder.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <DisciplineIcon className={`w-4 h-4 ${priorityStyle.iconColor}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {reminder.discipline_name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDueDate(reminder.due_date, reminder.days_until_due)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
              {reminders.length > 3 && (
                <p className="text-xs text-center text-muted-foreground">
                  +{reminders.length - 3} more reminders
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Full widget
  return (
    <Card className={className} data-testid="visit-reminders-widget">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Visit Reminders
            </CardTitle>
            <CardDescription>
              Stay on track with your care team visits
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowSettings(!showSettings)}
              data-testid="reminder-settings-btn"
            >
              <Settings className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={loadReminders}
              data-testid="refresh-reminders-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        {/* Notification Settings */}
        {showSettings && (
          <div className="mt-4 p-4 bg-slate-50 rounded-lg space-y-3">
            <h4 className="text-sm font-medium">Notification Preferences</h4>
            <div className="flex items-center justify-between">
              <Label htmlFor="push" className="flex items-center gap-2 text-sm">
                <Volume2 className="w-4 h-4" />
                Push Notifications
              </Label>
              <Switch
                id="push"
                checked={notificationPrefs.push_enabled}
                onCheckedChange={(v) => setNotificationPrefs({...notificationPrefs, push_enabled: v})}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="sms" className="flex items-center gap-2 text-sm">
                <MessageSquare className="w-4 h-4" />
                SMS Reminders
              </Label>
              <Switch
                id="sms"
                checked={notificationPrefs.sms_enabled}
                onCheckedChange={(v) => setNotificationPrefs({...notificationPrefs, sms_enabled: v})}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="email" className="flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4" />
                Email Reminders
              </Label>
              <Switch
                id="email"
                checked={notificationPrefs.email_enabled}
                onCheckedChange={(v) => setNotificationPrefs({...notificationPrefs, email_enabled: v})}
              />
            </div>
          </div>
        )}
      </CardHeader>

      <CardContent>
        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="text-center p-3 bg-red-50 rounded-lg">
            <p className="text-2xl font-bold text-red-600">
              {reminders.filter(r => r.priority === "high").length}
            </p>
            <p className="text-xs text-muted-foreground">Urgent</p>
          </div>
          <div className="text-center p-3 bg-amber-50 rounded-lg">
            <p className="text-2xl font-bold text-amber-600">
              {reminders.filter(r => r.days_until_due <= 1 && r.days_until_due >= 0).length}
            </p>
            <p className="text-xs text-muted-foreground">Due Soon</p>
          </div>
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <p className="text-2xl font-bold text-green-600">
              {schedule.length}
            </p>
            <p className="text-xs text-muted-foreground">This Week</p>
          </div>
        </div>

        {/* Reminders List */}
        <ScrollArea className="h-[400px] pr-2">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : reminders.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
              <p className="text-lg font-medium">All visits on track!</p>
              <p className="text-sm text-muted-foreground">
                You're up to date with all your care team visits
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {reminders.map((reminder) => {
                const DisciplineIcon = getDisciplineIcon(reminder.discipline);
                const priorityStyle = getPriorityStyle(reminder.priority);
                const PriorityIcon = priorityStyle.icon;
                
                return (
                  <div
                    key={reminder.id}
                    className={`p-4 rounded-lg border transition-all hover:shadow-md ${priorityStyle.bg}`}
                    data-testid={`reminder-${reminder.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${DISCIPLINE_COLORS[reminder.discipline] || "bg-gray-100"}`}>
                        <DisciplineIcon className="w-5 h-5" />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-medium">{reminder.discipline_name}</h4>
                          <Badge className={priorityStyle.badge}>
                            <PriorityIcon className="w-3 h-3 mr-1" />
                            {reminder.priority}
                          </Badge>
                        </div>
                        
                        <p className="text-sm text-muted-foreground mb-2">
                          {reminder.message}
                        </p>
                        
                        <div className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-4">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDueDate(reminder.due_date, reminder.days_until_due)}
                            </span>
                            {reminder.staff_name && (
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {reminder.staff_name}
                              </span>
                            )}
                          </div>
                          
                          {reminder.staff_phone && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 text-xs"
                              onClick={() => {
                                navigator.clipboard.writeText(reminder.staff_phone);
                                toast.success("Phone number copied!");
                              }}
                              data-testid={`call-staff-${reminder.id}`}
                            >
                              <Phone className="w-3 h-3 mr-1" />
                              {reminder.staff_phone}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {/* Upcoming Schedule Preview */}
        {schedule.length > 0 && (
          <div className="mt-6 pt-4 border-t">
            <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Upcoming This Week
            </h4>
            <div className="grid grid-cols-7 gap-1 text-center text-xs">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                <div key={day} className="font-medium text-muted-foreground p-1">
                  {day}
                </div>
              ))}
              {Array.from({ length: 7 }).map((_, i) => {
                const date = new Date();
                date.setDate(date.getDate() + i);
                const dateStr = date.toISOString().split("T")[0];
                const visitsOnDay = schedule.filter(s => s.due_date === dateStr);
                
                return (
                  <div
                    key={i}
                    className={`p-2 rounded ${
                      visitsOnDay.length > 0 
                        ? "bg-primary/10 text-primary font-medium" 
                        : "bg-slate-50"
                    }`}
                  >
                    <div>{date.getDate()}</div>
                    {visitsOnDay.length > 0 && (
                      <div className="text-[10px]">{visitsOnDay.length} visit{visitsOnDay.length > 1 ? "s" : ""}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
